"use client";

import { useState, useEffect } from "react";

/**
 * Returns true only after `isLoading` has been true for at least `delayMs`.
 * Prevents loader/skeleton flash on fast loads (< delayMs).
 * Pass 0 explicitly to opt out and show the loader immediately.
 *
 * @param isLoading - current loading state
 * @param delayMs - minimum time (ms) loading must be true before showing UI; defaults to 200
 */
export function useDelayedLoading(
  isLoading: boolean,
  delayMs: number = 200,
): boolean {
  const [showLoader, setShowLoader] = useState(false);
  const effectiveDelay = delayMs <= 0 ? 0 : delayMs;

  useEffect(() => {
    if (effectiveDelay === 0 || !isLoading) return;
    const timer = window.setTimeout(() => setShowLoader(true), effectiveDelay);
    return () => {
      window.clearTimeout(timer);
      setShowLoader(false);
    };
  }, [isLoading, effectiveDelay]);

  return effectiveDelay === 0 ? isLoading : showLoader;
}
